import React, { useEffect, useMemo, useRef, useState } from 'react';
import Main from '../../components/HOC/Main';
import TableAction from '../../components/TableAction';
import { confirm } from "../../components/ConformationDialog";
import myAxios from '../../lib/axios';
import zoomIcon from "../../assets/images/zoom.svg";
import meetIcon from "../../assets/images/meet.svg";
import teamsIcon from "../../assets/images/teams.svg";

const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
const dateFormatter = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
const timeFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

const TYPES = [
  { key: 'webinar', label: 'Webinars' },
  { key: 'seminar', label: 'Seminars' },
  { key: 'event', label: 'Events' }, // NEW
];

const STATUSES = [
  { key: '', label: 'All status' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
];

const navBtnStyle = {
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#374151',
  borderRadius: 8,
  width: 32,
  height: 32,
  cursor: 'pointer',
  lineHeight: '30px',
  textAlign: 'center'
};

const badgeStyle = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 6,
  background: '#e0f2fe',
  color: '#0369a1',
  fontSize: 11,
  lineHeight: 1.6,
  cursor: 'default',
  whiteSpace: 'nowrap',
};

const tooltipStyle = {
  position: 'fixed',
  zIndex: 50,
  maxWidth: 320,
  background: '#111827',
  color: '#fff',
  padding: '10px 12px',
  borderRadius: 8,
  boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
  fontSize: 12
};

const btnPrimary = {
  background: '#1C3557',
  color: '#fff',
  border: 0,
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
};

const btnGhost = {
  background: 'transparent',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
};

// Inputs
function TextField({ label, value, onChange, type = 'text', span = 1, placeholder, required, error }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: `span ${span}` }}>
      <span style={{ fontSize: 12, color: '#6b7280' }}>
        {label}{required ? '' : ''}
      </span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`,
          borderRadius: 8,
          padding: '9px 10px'
        }}
      />
      {error && <span style={{ color: '#ef4444', fontSize: 12 }}>{error}</span>}
    </label>
  );
}

// function TextArea({ label, value, onChange, span = 1, rows = 3, required, error }) {
//   return (
//     <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: `span ${span}` }}>
//       <span style={{ fontSize: 12, color: '#6b7280' }}>
//         {label}{required ? ' ' : ''}
//       </span>
//       <textarea
//         value={value ?? ''}
//         onChange={(e) => onChange(e.target.value)}
//         rows={rows}
//         style={{
//           border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`,
//           borderRadius: 8,
//           padding: '9px 10px',
//           resize: 'vertical'
//         }}
//       />
//       {error && <span style={{ color: '#ef4444', fontSize: 12 }}>{error}</span>}
//     </label>
//   );
// }

function useDebouncedValue(value, delay = 450) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// Date helpers
const buildEventDate = (e) => {
  try {
    return new Date(`${e.date}T${e.time}`);
  } catch {
    return new Date(e.date);
  }
};

const getMonthGrid = (cursor) => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const firstDay = new Date(year, month, 1);
  const startIndex = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = startIndex - 1; i >= 0; i--) cells.push({ key: `p-${i}`, day: prevMonthDays - i, muted: true, events: [] });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ key: `c-${d}`, day: d, muted: false, events: [] });
  const remaining = 42 - cells.length;
  for (let n = 1; n <= remaining; n++) cells.push({ key: `n-${n}`, day: n, muted: true, events: [] });

  return cells;
};

// File upload
function Dropzone({ file, previewUrl, onFile, onClear }) {
  const ref = useRef(null);

  const handleFiles = (files) => {
    console.log(files);
    const f = files instanceof FileList ? files[0] : files;
    if (!f) return;
    console.log(f);
    onFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      ref={ref}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={onDrop}
      onClick={() => ref.current?.querySelector('input')?.click()}
      style={{
        border: '1px dashed #d1d5db',
        borderRadius: 8,
        minHeight: 84,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files[0])}
      />
      {previewUrl ? (
        <>
          <img
            src={previewUrl}
            alt="Banner preview"
            style={{ width: '100%', height: 140, objectFit: 'cover' }}
          />
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); onClear(); }}
            style={{
              position: 'absolute', top: 8, right: 8,
              background: '#111827', color: '#fff', border: 0, borderRadius: 8,
              fontSize: 12, padding: '6px 8px', cursor: 'pointer'
            }}
          >
            Remove
          </button>
        </>
      ) : (
        <div className='flex flex-col items-center gap-2'>
          <svg width="30" height="22" viewBox="0 0 30 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.75 21.5C3.02344 21.5 0 18.4766 0 14.75C0 11.8062 1.88437 9.30313 4.50937 8.37969C4.50469 8.25313 4.5 8.12656 4.5 8C4.5 3.85625 7.85625 0.5 12 0.5C14.7797 0.5 17.2031 2.00937 18.5016 4.25937C19.2141 3.78125 20.0766 3.5 21 3.5C23.4844 3.5 25.5 5.51562 25.5 8C25.5 8.57187 25.3922 9.11563 25.2 9.62188C27.9375 10.175 30 12.5984 30 15.5C30 18.8141 27.3141 21.5 24 21.5H6.75ZM10.4531 11.3281C10.0125 11.7688 10.0125 12.4812 10.4531 12.9172C10.8938 13.3531 11.6062 13.3578 12.0422 12.9172L13.8703 11.0891V17.375C13.8703 17.9984 14.3719 18.5 14.9953 18.5C15.6187 18.5 16.1203 17.9984 16.1203 17.375V11.0891L17.9484 12.9172C18.3891 13.3578 19.1016 13.3578 19.5375 12.9172C19.9734 12.4766 19.9781 11.7641 19.5375 11.3281L15.7875 7.57812C15.3469 7.1375 14.6344 7.1375 14.1984 7.57812L10.4484 11.3281H10.4531Z" fill="#A3A3A3"/>
          </svg>
          <span>{file ? `Selected: ${file.name}` : 'Drag and drop files here or click to browse'}</span>
        </div>
      )}
    </div>
  );
}

function AddEditDialog({
  open, mode, activeTab, form, setForm, errors,
  onClose, onSave, saving,
  bannerFile, setBannerFile, bannerPreview, setBannerPreview,
  existingBannerUrl,
}) {
  useEffect(() => {
    if (!bannerFile) {
      setBannerPreview(existingBannerUrl || null);
      return;
    }
    if (bannerFile instanceof File || bannerFile instanceof Blob) {
      const url = URL.createObjectURL(bannerFile);
      setBannerPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setBannerPreview(existingBannerUrl || null);
    }
  }, [bannerFile, existingBannerUrl, setBannerPreview]);

  if (!open) return null;

  const typeLabelPlural = TYPES.find(t => t.key === activeTab)?.label || 'Events';
  const singular = (l) => (l?.endsWith('s') ? l.slice(0, -1) : l);
  const header = mode === 'create'
    ? `Add New ${singular(typeLabelPlural)} Event`
    : 'Edit Event';

  // const radio = (value, label, icon) => (
  //   <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 16, cursor: 'pointer' }}>
  //     <input
  //       type="radio"
  //       name="meeting_type"
  //       checked={form.meeting_type === value}
  //       onChange={() => setForm({ ...form, meeting_type: value })}
  //     />
  //     <span>{icon} {label}</span>
  //   </label>
  // );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 40
    }}>
      <div
        style={{
          width: 'clamp(340px, 38vw, 420px)',
          maxWidth: '95vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: 12,
          padding: 14
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 400 }}>{header}</div>
          <button onClick={onClose} style={{ border: 0, background: 'transparent', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>

        <div className="modal-scroll" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, overflowY: 'auto', paddingRight: 2 }}>
          <TextField  label={<span style={{ color: '#000000' }}>Event Title</span>}  value={form.title} onChange={(v) => setForm({ ...form, title: v })} required error={errors?.title} span={2} placeholder="Enter event title" />
          <TextField label={<span style={{ color: '#000000' }}>Date</span>} type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required error={errors?.date} />
          <TextField label={<span style={{ color: '#000000' }}>Time</span>} type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} required error={errors?.time} />
          <TextField label={<span style={{ color: '#000000' }}>Event Subtitle</span>} value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} required error={errors?.subtitle} span={2} placeholder="Enter organizer name" />

          <div style={{ gridColumn: 'span 2' }}>
            <span style={{ fontSize: 12, color: '#000000' }}>Add Banner</span>
            <div style={{ marginTop: 6 }}>
              <Dropzone
                file={bannerFile}
                previewUrl={bannerPreview}
                onFile={(f) => {
                  setBannerFile(f);
                  setBannerPreview(URL.createObjectURL(f));
                }}
                onClear={() => {
                  setBannerFile(null);
                  setBannerPreview(null);
                }}
              />
            </div>
          </div>

          {/* <TextField label="Organizer name" value={form.organizer_name} onChange={(v) => setForm({ ...form, organizer_name: v })} required error={errors?.organizer_name} span={2} placeholder="Enter organizer name" /> */}
          <TextField label={<span style={{ color: '#000000' }}>No. of seats</span>} type="number" value={form.total_seats} onChange={(v) => setForm({ ...form, total_seats: Number(v) })} required error={errors?.total_seats} span={2} placeholder="No. of seats available" />
          <TextField style={{ height: "100px" }} label={<span style={{ color: '#000000' }}>Main Body</span>} value={form.summary} onChange={(v) => setForm({ ...form, summary: v })} required error={errors?.summary} span={2} placeholder="Summary" />

          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ fontSize: 12, color: '#000000', marginBottom: 6 }}>Meeting type </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="meeting_type" value="zoom" />
                <img src={zoomIcon} alt="Zoom" width={16} style={{ display: 'inline-block' }} />
                Zoom
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="meeting_type" value="google_meet" />
                <img src={meetIcon} alt="Google Meet" width={16} style={{ display: 'inline-block' }} />
                Google Meet
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="meeting_type" value="teams" />
                <img src={teamsIcon} alt="Teams" width={16} style={{ display: 'inline-block' }} />
                Microsoft Teams
              </label>
            </div>

            {errors?.meeting_type && (
              <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>
                {errors.meeting_type}
              </div>
            )}
          </div>

          {/* <TextField label="Meeting link" value={form.meeting_link} onChange={(v) => setForm({ ...form, meeting_link: v })} required error={errors?.meeting_link} span={2} placeholder="https://..." /> */}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} style={btnGhost} disabled={saving}>Cancel</button>
          <button onClick={onSave} style={btnPrimary} disabled={saving}>
            {mode === 'create' ? `Save ${singular(typeLabelPlural)}` : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [activeTab, setActiveTab] = useState('webinar');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const searchQ = useDebouncedValue(search, 450);

  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tip, setTip] = useState(null);
  const tipTimer = useRef(null);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [editBannerPreview, setEditBannerPreview] = useState(null);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [createErrors, setCreateErrors] = useState({});
  const [createBanner, setCreateBanner] = useState(null);
  const [createBannerPreview, setCreateBannerPreview] = useState(null);
  const [savingCreate, setSavingCreate] = useState(false);

  const monthLabel = useMemo(() => monthFormatter.format(cursor), [cursor]);
  const activeTypeLabel = useMemo(
    () => TYPES.find(t => t.key === activeTab)?.label || 'Events',
    [activeTab]
  );

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = { type: activeTab, status: status || undefined, q: searchQ || undefined };
      const res = await myAxios.get('/admin/events', { params });
      const list = Array.isArray(res?.data?.data) ? res.data.data :
                   Array.isArray(res?.data) ? res.data : [];
      list.sort((a, b) => buildEventDate(a) - buildEventDate(b));
      setEvents(list);
    } catch (e) {
      console.error('Fetch events error', e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEvents(); }, [activeTab, status, searchQ, cursor]);

  const calendar = useMemo(() => {
    const grid = getMonthGrid(cursor);
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthEvents = events
      .filter((e) => e.type === activeTab)
      .filter((e) => {
        const d = buildEventDate(e);
        return d.getFullYear() === year && d.getMonth() === month;
      });
    const byDay = monthEvents.reduce((acc, ev) => {
      const day = buildEventDate(ev).getDate();
      acc[day] = acc[day] || [];
      acc[day].push(ev);
      return acc;
    }, {});
    let start = 0;
    while (start < grid.length && grid[start].muted) start++;
    const daysInCurMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInCurMonth; d++) {
      const idx = start + (d - 1);
      if (grid[idx]) grid[idx].events = byDay[d] || [];
    }
    return grid;
  }, [cursor, events, activeTab]);

  const upcoming = useMemo(() =>
    events.filter((e) => e.type === activeTab)
          .sort((a, b) => buildEventDate(a) - buildEventDate(b)),
    [events, activeTab]
  );

  const onDelete = async (item) => {
    const result = await confirm({
      message: "Are you sure you want to delete this event?",
    });

    if (!result) return;

    try {
      await myAxios.delete(`/admin/events/${item.event_id}`);
      await fetchEvents();
    } catch (e) {
      console.error("Delete error", e);
    }
  };

  const openEdit = (item) => {
    const base = process.env.REACT_APP_BASE_URL.replace(/\/tgs$/, "");
    setEditing(item);
    setEditForm({
      title: item.title || '',
      subtitle: item.subtitle || '',
      organizer_name: item.organizer_name || '',
      date: item.date || '',
      time: item.time || '',
      total_seats: item.total_seats || 0,
      meeting_type: item.meeting_type || 'zoom',
      meeting_link: item.meeting_link || '',
      status: item.status || 'draft',
      summary: item.summary || '',
      banner_url: base+item.banner_url || null,
    });
    setEditBanner(null);
    console.log(base+item.banner_url);
    setEditBannerPreview(base+item.banner_url || null);
  };

  const uploadBanner = async (eventId, file) => {
    if (!(file instanceof File || file instanceof Blob)) return;
    const form = new FormData();
    form.append('banner', file);
    await myAxios.post(`/admin/events/${eventId}/banner`, form);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      editForm.banner_url = editForm.banner_url.replace(/^.*(?=\/assets)/, "");
      await myAxios.put(`/admin/events/${editing.event_id}`, editForm);
      if (editBanner) await uploadBanner(editing.event_id, editBanner);
      setEditing(null);
      await fetchEvents();
    } catch (e) {
      console.error('Update error', e);
    } finally {
      setSavingEdit(false);
    }
  };


  const openCreate = () => {
    setCreateForm({
      title: '',
      subtitle: '',
      organizer_name: '',
      date: '',
      time: '',
      total_seats: '',
      meeting_type: 'zoom',
      meeting_link: '',
      type: activeTab,
      status: 'published',
      summary: '',
    });
    setCreateBanner(null);
    setCreateBannerPreview(null);
    setCreateErrors({});
    setCreating(true);
  };

  const validateCreate = (f) => {
    const errs = {};
    if (!f.title?.trim()) errs.title = 'Required';
    if (!f.date) errs.date = 'Required';
    if (!f.time) errs.time = 'Required';
    if (!f.subtitle?.trim()) errs.subtitle = 'Required';
    if (!f.total_seats || Number(f.total_seats) <= 0) errs.total_seats = 'Required';
    if (!f.summary?.trim()) errs.summary = 'Required';
    if (!f.meeting_type) errs.meeting_type = 'Required';
    return errs;
  };

  const saveCreate = async () => {
    const payload = { ...createForm, type: activeTab };
    const errs = validateCreate(payload);
    setCreateErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSavingCreate(true);
    try {
      const res = await myAxios.post('/admin/events/', payload);
      const created = res?.data?.data || res?.data;
      const eventId = created?.event_id;

      if (eventId && createBanner) await uploadBanner(eventId, createBanner);

      setCreating(false);
      await fetchEvents();
    } catch (e) {
      console.error('Create error', e);
    } finally {
      setSavingCreate(false);
    }
  };

  

  const prevMonth = () => setCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const closeTipSoon = () => {
    clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setTip(null), 120);
  };

  const formatDateLabel = (e) => dateFormatter.format(buildEventDate(e));
  const formatTimeLabel = (e) => timeFormatter.format(buildEventDate(e));

  return (
    <Main crumbs={[{ title: 'networking' }, { title: 'management' }]}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users..."
          style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', outline: 'none' }}
          aria-label="Search users"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ display: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#fff' }}
          aria-label="Filter by status"
        >
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button
          type="button"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1C3557', color: '#fff', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }}
          onClick={openCreate}
        >
          <span style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1C3557', borderRadius: 4 }}>+</span>
          Add new
        </button>
      </div>

      <div role="tablist" aria-label="Event types" style={{ display: 'flex', gap: 16, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        {TYPES.map((t) => {
          const selected = activeTab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={selected}
              aria-controls={`panel-${t.key}`}
              id={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '10px 2px',
                borderBottom: selected ? '2px solid #0ea5e9' : '2px solid transparent',
                color: selected ? '#0ea5e9' : '#6b7280',
                cursor: 'pointer',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>{monthLabel}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={prevMonth} aria-label="Previous month" style={navBtnStyle}>{'<'}</button>
            <button onClick={nextMonth} aria-label="Next month" style={navBtnStyle}>{'>'}</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6, color: '#6b7280', fontSize: 12 }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
            <div key={d} style={{ textAlign: 'center' }}>{d}</div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {calendar.map((cell) => (
            <div
              key={cell.key}
              style={{
                minHeight: 72,
                border: '1px solid #f3f4f6',
                borderRadius: 6,
                background: cell.muted ? '#fafafa' : '#fff',
                color: cell.muted ? '#9ca3af' : '#111827',
                padding: 6,
                fontSize: 12,
              }}
            >
              <div style={{ marginBottom: 6 }}>{cell.day}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {cell.events.slice(0, 3).map((ev) => (
                  <span
                    key={ev.event_id}
                    style={badgeStyle}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTip({
                        x: rect.left + rect.width / 2,
                        y: rect.bottom + 6,
                        event: ev
                      });
                      clearTimeout(tipTimer.current);
                    }}
                    onMouseLeave={closeTipSoon}
                  >
                    {ev.title}
                  </span>
                ))}
                {cell.events.length > 3 && (
                  <span style={{ ...badgeStyle, background: '#fee2e2', color: '#991b1b' }}>
                    +{cell.events.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>
          <span>Upcoming {activeTypeLabel}</span>
          <span>body</span>
        </div>

        <div style={{ padding: 12 }}>
          {loading && <div style={{ color: '#6b7280', fontSize: 14 }}>Loading...</div>}
          {!loading && upcoming.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 14 }}>No upcoming {activeTypeLabel.toLowerCase()} yet.</div>
          )}

          {upcoming.map((ev) => (
            <div key={ev.event_id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', border: '1px solid #f3f4f6', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span role="img" aria-label="calendar">📅</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 600 }}>{ev.title}</div>
                  <TableAction
                    handleDeleteButton={() => onDelete(ev)}
                    handleEditButton={() => openEdit(ev)}
                  />
                </div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6, display: 'flex', gap: 16 }}>
                  <span>ID: {ev.event_id}</span>
                  <span>Date: {formatDateLabel(ev)}</span>
                  <span>Time: {formatTimeLabel(ev)}</span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
                  Subtitle: {ev.subtitle || '-'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {tip && (
        <div
          style={{
            ...tooltipStyle,
            left: Math.max(12, Math.min(tip.x - 140, window.innerWidth - 340)),
            top: tip.y
          }}
          onMouseEnter={() => clearTimeout(tipTimer.current)}
          onMouseLeave={closeTipSoon}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{tip.event.title}</div>
          <div>{tip.event.type?.toUpperCase() + tip.event.type?.slice(1)}</div>
          <div>{formatDateLabel(tip.event)} • {formatTimeLabel(tip.event)}</div>
          {tip.event.subtitle && <div style={{ marginTop: 4, color: '#d1d5db' }}>{tip.event.subtitle}</div>}
          {tip.event.organizer_name && <div style={{ marginTop: 2, color: '#d1d5db' }}>Organizer: {tip.event.organizer_name}</div>}
          {!!tip.event.meeting_type && !!tip.event.meeting_link && (
            <div style={{ marginTop: 2, color: '#d1d5db' }}>{tip.event.meeting_type}: {tip.event.meeting_link}</div>
          )}
          {typeof tip.event.seats_available === 'number' && typeof tip.event.total_seats === 'number' && (
            <div style={{ marginTop: 2, color: '#d1d5db' }}>
              Seats: {tip.event.seats_available}/{tip.event.total_seats}
            </div>
          )}
        </div>
      )}

      <AddEditDialog
        open={creating}
        mode="create"
        activeTab={activeTab}
        form={createForm}
        setForm={setCreateForm}
        errors={createErrors}
        onClose={() => setCreating(false)}
        onSave={saveCreate}
        saving={savingCreate}
        bannerFile={createBanner}
        setBannerFile={setCreateBanner}
        bannerPreview={createBannerPreview}
        setBannerPreview={setCreateBannerPreview}
      />

      {editing && (
        <AddEditDialog
          open={!!editing}
          mode="edit"
          activeTab={activeTab}
          form={editForm}
          setForm={setEditForm}
          errors={{}}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
          saving={savingEdit}
          bannerFile={editBanner}
          setBannerFile={setEditBanner}
          bannerPreview={editBannerPreview}
          setBannerPreview={setEditBannerPreview}
          existingBannerUrl={editForm.banner_url}
        />
      )}
    </Main>
  );
}

export { EventsPage };
